import { useCallback, useEffect, useRef, useState } from "react";
import { createSession, streamReply, sendFeedback, sendToolResult } from "../api/assistant";
import { franc } from "franc-min";

const API_BASE = process.env.REACT_APP_API_BASE;

const SESSION_KEY = "assistant.sessionId";
const CONSENT_KEY = "assistant.consent";

/** Auto language detection (fr/de/en) with sensible fallbacks */
function detectLang(text) {
  if (!text || text.trim().length < 2) return "en";
  const iso = franc(text); // "fra","deu","eng","und", ...
  if (iso === "fra") return "fr";
  if (iso === "deu") return "de";
  if (iso === "eng") return "en";

  // Fallback: browser language
  const nav = (navigator.language || "").toLowerCase();
  if (nav.startsWith("fr")) return "fr";
  if (nav.startsWith("de")) return "de";
  return "en";
}

export function useAssistantSSE(defaultLang = "auto") {
  // ---------------- State / Refs (TOP-LEVEL ONLY) ----------------
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(localStorage.getItem(SESSION_KEY) || null);
  const [consent, setConsent] = useState(JSON.parse(localStorage.getItem(CONSENT_KEY) ?? "null"));
  const [isStreaming, setIsStreaming] = useState(false);
  const [lang, setLang] = useState(defaultLang); // visible UI lang (AUTO/FR/DE/EN)

  const controllerRef = useRef(null);
  const lastRoomsRef = useRef(null);
  /** Locks the language used for the CURRENT user turn (stable across inline tool) */
  const lastLangRef = useRef("auto");

  // ---------------- Session bootstrap ----------------
  useEffect(() => {
    (async () => {
      if (!sessionId) {
        try {
          const id = await createSession({ lang, consent: consent === true });
          localStorage.setItem(SESSION_KEY, id);
          setSessionId(id);
        } catch (err) {
          console.error("Failed to auto-create session:", err);
        }
      }
    })();
    // eslint-disable-next-line
  }, [lang]);

  const giveConsent = useCallback(
    async (value) => {
      setConsent(value);
      localStorage.setItem(CONSENT_KEY, JSON.stringify(value));

      if (value && !sessionId) {
        const id = await createSession({ lang, consent: true });
        localStorage.setItem(SESSION_KEY, id);
        setSessionId(id);
      }
    },
    [lang, sessionId]
  );

  // ---------------- Send message (Phase 1) ----------------
  const sendMessage = useCallback(
    async (text) => {
      if (!text?.trim()) return;
      if (!consent) throw new Error("Consent required");

      // Detect & lock language for this turn
      const detected = lang === "auto" ? detectLang(text) : lang;
      lastLangRef.current = detected;         // hard lock for this user turn
      setLang(detected);                      // reflect in UI state too

      // Ensure session
      if (!sessionId) {
        const id = await createSession({ lang: detected, consent: true });
        localStorage.setItem(SESSION_KEY, id);
        setSessionId(id);
      }

      // Append user message
      const userMsg = { id: crypto.randomUUID(), role: "user", content: text };
      setMessages((m) => [...m, userMsg]);

      // Placeholder assistant message for streaming
      const asstId = crypto.randomUUID();
      setMessages((m) => [...m, { id: asstId, role: "assistant", content: "" }]);
      setIsStreaming(true);

      // Abort any prior stream
      controllerRef.current?.abort();
      controllerRef.current = new AbortController();

      const { cancel } = streamReply({
        sessionId,
        text,
        lang: detected, // 🚩 critical: backend will honor this strictly
        signal: controllerRef.current.signal,

        onToken: (tok) => {
          if (!tok) return; // Schutz gegen leere Tokens
          
          setMessages((m) =>
            m.map((msg) =>
              msg.id === asstId ? { ...msg, content: msg.content + tok } : msg
            )
          );
          maybeTriggerInlineTool(asstId);
        },

        onSources: (src) => {
          setMessages((m) =>
            m.map((msg) => (msg.id === asstId ? { ...msg, sources: src } : msg))
          );
        },

        onToolCall: (toolJson) => {
          try {
            const obj = JSON.parse(toolJson);
            // Use the locked lang for this turn
            handleToolCall(obj, asstId, lastLangRef.current);
          } catch (e) {
            console.warn("Failed to parse tool_call payload:", e, toolJson);
          }
        },

        onDone: () => setIsStreaming(false),
        onError: () => setIsStreaming(false),
      });

      return { cancel };
    },
    [consent, sessionId, lang]
  );

  // ---------------- Inline JSON tool detection ----------------
  function maybeTriggerInlineTool(assistantMsgId) {
    setMessages((m) => {
      const idx = m.findIndex((x) => x.id === assistantMsgId);
      if (idx < 0) return m;
      const msg = m[idx];
      const t = msg.content.trim();

      // Take the last JSON-like block
      const match = t.match(/\{[\s\S]*\}$/);
      if (!match) return m;

      try {
        const obj = JSON.parse(match[0]);
        if (obj && typeof obj === "object" && obj.tool && obj.arguments) {
          // ⚠️ use the language locked at user-turn time, not the possibly-updated state
          handleToolCall(obj, assistantMsgId, lastLangRef.current);
          const mm = [...m];
          mm[idx] = { ...msg, content: "…" }; // temporary until Phase 2 returns
          return mm;
        }
      } catch {
        // ignore parsing errors
      }
      return m;
    });
  }

  // ---------------- Tool call handling (Phase 1 -> client tool -> Phase 2) ----------------
  async function handleToolCall(toolCall, assistantMsgId, usedLang) {
    const { tool, arguments: args } = toolCall;

    let result;
    try {
      if (tool === "searchRooms") result = await runSearchRooms(args || {});
      else if (tool === "bookVisit") result = await runBookVisit(args || {});
      else if (tool === "contactOwner") result = await runContactOwner(args || {});
      else result = { error: "Unknown tool" };
    } catch (err) {
      console.error("Tool error:", err);
      result = { error: err.message || String(err) };
    }

    // Optional: preview the raw result while Phase 2 formats the text
    pushToolPreview(assistantMsgId, result);

    // Normalize arrays
    const safeResult = Array.isArray(result) ? { rooms: result } : result;

    // Phase 2: send to backend so LLM formats a clean answer (in usedLang)
    const final = await sendToolResult({
      sessionId,
      tool,
      result: safeResult,
      lang: usedLang,
    });

    // Replace placeholder with final text
    setMessages((m) =>
      m.map((msg) => (msg.id === assistantMsgId ? { ...msg, content: final.text } : msg))
    );
  }

  function pushToolPreview(msgId, result) {
    setMessages((m) =>
      m.map((msg) => (msg.id === msgId ? { ...msg, toolResult: result } : msg))
    );
  }

  // ---------------- Tools (call your backend REST) ----------------
  async function runSearchRooms({ country, city, type, district, minPrice, maxPrice }) {
    const deburr = (s) =>
      s ? s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase() : "";

    const COUNTRY_MAP = {
      cameroun: "Cameroon",
      cameroon: "Cameroon",
      kamerun: "Cameroon",
      allemagne: "Germany",
      deutschland: "Germany",
      germany: "Germany",
    };

    const CITY_MAP = {
      yaounde: "Yaounde",
      "yaoundé": "Yaounde",
      yaoundé: "Yaounde",
      douala: "Douala",
      worms: "Worms",
      berlin: "Berlin",
    };

    const normCity = city ? CITY_MAP[deburr(String(city))] || city : null;
    let normCountry = country ? COUNTRY_MAP[deburr(String(country))] || country : null;

    // Infer country for known CM cities
    if (!normCountry && normCity && ["Douala", "Yaounde"].includes(normCity)) normCountry = "Cameroon";

    const TYPE_MAP = {
      maison: "Villa",
      house: "Villa",
      haus: "Villa",
      villa: "Villa",
      appartement: "Apartment",
      apartment: "Apartment",
      wohnung: "Apartment",
      studio: "Studio",
      chambre: "Room",
      room: "Room",
      zimmer: "Room",
      meuble: "Furnished",
      "meublé": "Furnished",
      furnished: "Furnished",
      "möbliert": "Furnished",
      hotel: "Hotel",
      "hôtel": "Hotel",
    };

    const userSpecifiedType = Boolean(type && String(type).trim());
    const mappedType = userSpecifiedType ? TYPE_MAP[deburr(String(type))] || type : null;

    const makeUrl = (includeType) => {
      const params = new URLSearchParams();
      const tokens = [];
      if (normCountry) tokens.push(normCountry);
      if (normCity) tokens.push(normCity);
      if (district) tokens.push(String(district));
      if (tokens.length) params.append("SearchText", tokens.join(" "));
      if (includeType && mappedType) params.append("Type", mappedType);
      if (minPrice != null) params.append("MinPrice", String(minPrice));
      if (maxPrice != null) params.append("MaxPrice", String(maxPrice));
      params.append("PageNumber", "1");
      params.append("PageSize", "50");
      return `${API_BASE}/api/Room/filter?${params.toString()}`;
    };

    // Try with type (if provided)
    let url = makeUrl(true);
    let res = await fetch(url);
    if (!res.ok) throw new Error("Room search failed");
    let data = await res.json();

    // Auto-retry without type if no results and type was applied
    const total = data?.totalCount ?? data?.TotalCount ?? (Array.isArray(data?.items) ? data.items.length : 0);
    if (total === 0 && userSpecifiedType) {
      url = makeUrl(false);
      res = await fetch(url);
      if (res.ok) data = await res.json();
    }

    lastRoomsRef.current = data;
    return data;
  }

  async function runBookVisit(body) {
    const res = await fetch(`${API_BASE}/api/Visit/book`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error("Booking failed");
    return await res.json();
  }

  /**
   * Contact owner:
   * - Prefer hostId directly
   * - Else derive hostId from last search result + roomId
   * - Then call /api/Room/by-host?hostId=...
   */
  async function runContactOwner({ hostId, roomId }) {
    let finalHostId = hostId;

    if (!finalHostId && roomId && lastRoomsRef.current) {
      const items =
        lastRoomsRef.current.items ??
        lastRoomsRef.current.Items ??
        lastRoomsRef.current.rooms ??
        (Array.isArray(lastRoomsRef.current) ? lastRoomsRef.current : []);
      const match = items.find((r) => (r.id ?? r.Id) === roomId || (r.roomId ?? r.RoomId) === roomId);
      finalHostId = match?.hostId ?? match?.HostId ?? match?.hostID ?? null;
    }

    if (!finalHostId) throw new Error("HostId could not be resolved.");

    const url = `${API_BASE}/api/Room/by-host?hostId=${encodeURIComponent(
      finalHostId
    )}&pageNumber=1&pageSize=1`;

    const res = await fetch(url);
    if (!res.ok) throw new Error("Failed to load owner");
    const data = await res.json();

    return {
      hostId: finalHostId,
      rooms: data.items ?? data.Items ?? [],
      contactUrl: `/messages/new?to=${finalHostId}`,
    };
  }

  // ---------------- Feedback & controls ----------------
  const sendCsat = useCallback(
    async ({ csat, note }) => {
      if (!sessionId) return;
      await sendFeedback({ sessionId, csat, note });
    },
    [sessionId]
  );

  const stop = useCallback(() => {
    controllerRef.current?.abort();
    setIsStreaming(false);
  }, []);

  // ---------------- Expose API ----------------
  return {
    messages,
    isStreaming,
    sendMessage,
    stop,
    consent,
    giveConsent,
    lang,
    setLang,
    sessionId,
    sendCsat,
  };
}
